// src/business/services/businessProductsService.js
import { request, ROUTES } from "../../services/api";
import {
  uploadBusinessProduct3DModelAsset,
  uploadBusinessProduct3DPosterAsset,
  uploadBusinessProductImageAsset,
} from "../../services/assetUploadService";

/**
 * Business Products Service (backend-ready)
 *
 * - Uses ROUTES.businessProducts, ROUTES.businessProductById...
 * - Keeps fallback logic for draft endpoints (404),
 *   because backend might not have draft endpoints yet.
 */

function resolveRoute(key, ...args) {
  const r = ROUTES?.[key];
  if (!r) return undefined;
  return typeof r === "function" ? r(...args) : r;
}

function is404(err) {
  if (err?.response?.status === 404) return true;
  const msg = String(err?.message || "");
  return msg.includes("Request failed: 404") || msg.includes("404") || msg.includes("Not Found");
}

function pickId(res) {
  return (
    res?.id ||
    res?.draftId ||
    res?.productId ||
    res?.data?.id ||
    res?.data?.draftId ||
    res?.data?.productId ||
    res?.data?._id ||
    res?._id ||
    null
  );
}

export const businessProductsAPI = {
  /** ---------------- CRUD ---------------- */
  list: (params) =>
    request({
      url: resolveRoute("businessProducts"),
      method: "GET",
      params,
    }),

  listReviews: (params) =>
    request({
      url: resolveRoute("businessProductReviews"),
      method: "GET",
      params,
    }),

  replyToReview: (reviewId, payload) => {
    if (!reviewId) throw new Error("Request failed: Missing review id");
    return request({
      url: resolveRoute("businessProductReviewReply", reviewId),
      method: "POST",
      data: payload,
    });
  },

  getById: (id) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: resolveRoute("businessProductById", id),
      method: "GET",
    });
  },

  create: (data) =>
    request({
      url: resolveRoute("businessProducts"),
      method: "POST",
      data,
    }),

  update: (id, data) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: resolveRoute("businessProductById", id),
      method: "PUT",
      data,
    });
  },

  remove: (id) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: resolveRoute("businessProductById", id),
      method: "DELETE",
    });
  },

  /** ------------ Draft workflow (Wizard) ------------ */
  createDraft: async (data = { status: "DRAFT" }) => {
    const res = await request({
      url: resolveRoute("businessProductDraftCreate"),
      method: "POST",
      data,
    });
    const id = pickId(res);
    return id ? { ...res, id } : res;
  },

  updateDraft: async (draftId, data) => {
    if (!draftId) throw new Error("Request failed: Missing draft id");

    try {
      return await request({
        url: resolveRoute("businessProductDraftUpdate", draftId),
        method: "PATCH",
        data,
      });
    } catch (err) {
      if (!is404(err)) throw err;

      return request({
        url: resolveRoute("businessProductById", draftId),
        method: "PUT",
        data,
      });
    }
  },

  publishDraft: async (draftId, data) => {
    if (!draftId) throw new Error("Request failed: Missing draft id");

    try {
      return await request({
        url: resolveRoute("businessProductDraftPublish", draftId),
        method: "POST",
        data,
      });
    } catch (err) {
      if (!is404(err)) throw err;

      return request({
        url: resolveRoute("businessProductById", draftId),
        method: "PUT",
        data: { ...(data || {}), status: data?.status || "LISTED" },
      });
    }
  },

  /** ------------ Status change ------------ */
  updateStatus: (id, status) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: resolveRoute("businessProductStatus", id),
      method: "PATCH",
      data: { status },
    });
  },
  /** ---------------- Upload image ---------------- */
  uploadImage: (file, meta = {}) => {
    if (!file) throw new Error("Request failed: Missing file");
    return uploadBusinessProductImageAsset(file, meta);
  },

  uploadVariant3DModel: (file, meta = {}) => {
    if (!file) throw new Error("Request failed: Missing file");
    return uploadBusinessProduct3DModelAsset(file, meta);
  },

  uploadVariant3DPoster: (file, meta = {}) => {
    if (!file) throw new Error("Request failed: Missing file");
    return uploadBusinessProduct3DPosterAsset(file, meta);
  },

  upsertVariant3D: (id, data) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: resolveRoute("businessProductVariant3D", id),
      method: "PUT",
      data,
    });
  },

  listVariant3DAssets: (id) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: resolveRoute("businessProductVariant3D", id),
      method: "GET",
    });
  },

  removeVariant3D: (id, data) => {
    if (!id) throw new Error("Request failed: Missing product id");
    return request({
      url: resolveRoute("businessProductVariant3D", id),
      method: "DELETE",
      data,
    });
  },

  /** ---------------- Categories ---------------- */
  getCategories: () =>
    request({
      url: resolveRoute("businessCategories"),
      method: "GET",
    }),

  createCategory: (data) =>
    request({
      url: resolveRoute("businessCategories"),
      method: "POST",
      data,
    }),

  updateCategory: (id, data) => {
    if (!id) throw new Error("Request failed: Missing category id");
    return request({
      url: resolveRoute("businessCategoryById", id),
      method: "PATCH",
      data,
    });
  },

  deactivateCategory: (id) => {
    if (!id) throw new Error("Request failed: Missing category id");
    return request({
      url: resolveRoute("businessCategoryById", id),
      method: "DELETE",
    });
  },

  /** ---------------- Tags ---------------- */
  getTags: () =>
    request({
      url: resolveRoute("businessTags"),
      method: "GET",
    }),

  createTag: (data) =>
    request({
      url: resolveRoute("businessTags"),
      method: "POST",
      data,
    }),

  updateTag: (id, data) => {
    if (!id) throw new Error("Request failed: Missing tag id");
    return request({
      url: resolveRoute("businessTagById", id),
      method: "PATCH",
      data,
    });
  },

  archiveTag: (id) => {
    if (!id) throw new Error("Request failed: Missing tag id");
    return request({
      url: resolveRoute("businessTagById", id),
      method: "DELETE",
    });
  },

  /** ---------------- Sales Report ---------------- */
  getProductSalesReport: () =>
    request({
      url: resolveRoute("businessProductSales"),
      method: "GET",
    }),
};
